import { CalendarIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Separator, XStack, YStack } from 'tamagui';

import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '../../components/buttons/Button';
import { DatePicker } from '../../components/date/DatePicker';
import { DateRangeSelectionSheet } from '../../components/date/DateRangeSelectionSheet';
import { DateSelectionSheet } from '../../components/date/DateSelectionSheet';
import { InputTrigger } from '../../components/inputs/InputTrigger';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
} from '../../components/text/Typography';
import { format } from '../../utils/dateUtils';

const DATE_INPUT_FORMAT = 'MM/dd/yyyy';

export const DateSelections = () => {
    const [pickerDate, setPickerDate] = useState(new Date());

    const [singleDate, setSingleDate] = useState(new Date());
    const [isSingleDateSheetOpen, setIsSingleDateSheetOpen] = useState(false);

    const [fromDate, setFromDate] = useState(new Date('2025-01-01'));
    const [toDate, setToDate] = useState(new Date());
    const [isDateRangeSheetOpen, setIsDateRangeSheetOpen] = useState(false);

    return (
        <YStack gap={48}>
            <YStack gap={8}>
                <BodyMediumEmphasized>
                    DateSelectionSheet connected to DateInput
                </BodyMediumEmphasized>
                <BodySmallRegular>
                    Tap the DateInput to open the single-date sheet.
                </BodySmallRegular>
                <InputTrigger
                    value={format(singleDate, DATE_INPUT_FORMAT)}
                    rightIcon={CalendarIcon}
                    onTrigger={() => {
                        setIsSingleDateSheetOpen(true);
                    }}
                />
                <Button
                    text="Open DateSelectionSheet"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    onPress={() => {
                        setIsSingleDateSheetOpen(true);
                    }}
                />
            </YStack>

            <Separator />

            <YStack gap={8}>
                <BodyMediumEmphasized>
                    DateRangeSelectionSheet connected to DateInputs
                </BodyMediumEmphasized>
                <BodySmallRegular>
                    Tap either DateInput to open the range sheet.
                </BodySmallRegular>
                <XStack gap={8}>
                    <InputTrigger
                        value={format(fromDate, DATE_INPUT_FORMAT)}
                        rightIcon={CalendarIcon}
                        placeholder="Start date"
                        onTrigger={() => {
                            setIsDateRangeSheetOpen(true);
                        }}
                        containerProps={{
                            flex: 1,
                        }}
                    />
                    <InputTrigger
                        value={format(toDate, DATE_INPUT_FORMAT)}
                        rightIcon={CalendarIcon}
                        placeholder="End date"
                        onTrigger={() => {
                            setIsDateRangeSheetOpen(true);
                        }}
                        containerProps={{
                            flex: 1,
                        }}
                    />
                </XStack>
                <Button
                    text="Open DateRangeSelectionSheet"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    onPress={() => {
                        setIsDateRangeSheetOpen(true);
                    }}
                />
            </YStack>

            <Separator />

            <YStack gap={8}>
                <BodyMediumEmphasized>DatePicker</BodyMediumEmphasized>
                <BodySmallRegular>
                    Direct date selection with platform-appropriate picker UI.
                </BodySmallRegular>
                <DatePicker value={pickerDate} onChange={setPickerDate} />
                <BodySmallRegular>
                    Selected: {format(pickerDate, DATE_INPUT_FORMAT)}
                </BodySmallRegular>
            </YStack>

            <DateSelectionSheet
                title="Select date"
                isOpen={isSingleDateSheetOpen}
                onClose={() => {
                    setIsSingleDateSheetOpen(false);
                }}
                defaultValue={singleDate}
                onConfirm={setSingleDate}
                confirmButtonText="Set date"
            />
            <DateRangeSelectionSheet
                isOpen={isDateRangeSheetOpen}
                onClose={() => {
                    setIsDateRangeSheetOpen(false);
                }}
                defaultFromDate={fromDate}
                defaultToDate={toDate}
                onConfirm={(nextFromDate, nextToDate) => {
                    setFromDate(nextFromDate);
                    setToDate(nextToDate);
                }}
                confirmButtonText="Set range"
            />
        </YStack>
    );
};
